import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

export const MARKET_FIELDS = [
  ["name", "Name"], ["email", "Email", true], ["property_address", "Address", true], ["city", "City"], ["state", "State"],
  ["zip_code", "ZIP"], ["phone", "Phone", true], ["tcpa_opt_in", "TCPA opt-in"], ["employment_type", "Employment type"],
  ["income", "Income"], ["military_status", "Military status"], ["credit", "Credit"], ["bankruptcy", "Bankruptcy"],
  ["requested_loan_amount", "Loan amount"], ["requested_loan_type", "Mortgage type"], ["property_use", "Property use"],
  ["property_description", "Property description"], ["estimated_property_value", "Market value"], ["rate_type", "Rate type"],
  ["foreclosure", "Foreclosure"], ["down_payment", "Down payment"], ["buy_timeframe", "Buy timeframe"], ["buyers_agent_found", "Buyer's agent found"],
  ["estimated_equity_amount", "Equity"], ["estimated_current_rate", "Inferred interest rate"], ["homeowner_age", "Homeowner's age"],
  ["lead_source_url", "Lead source URL"], ["lead_exclusivity", "Shared or exclusive"],
];

export const MarketLeadEditor = ({ m, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  if (m.status !== "available" && m.status !== "withdrawn") return null;
  const start = () => { setVals(Object.fromEntries(MARKET_FIELDS.map(([k]) => [k, m.data?.[k] ?? ""]))); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try { await api.post(`/admin/marketplace/leads/${m.id}/data`, { fields: vals }); toast.success("Lead details saved"); setOpen(false); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <>
      <Button size="sm" variant="ghost" onClick={start} data-testid={`market-edit-${m.id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="market-edit-dialog">
          <DialogHeader><DialogTitle>Edit Marketplace lead</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Fields marked "after purchase" stay hidden from MLOs until they buy the lead.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {MARKET_FIELDS.map(([k, label, hidden]) => (
              <label key={k} className="text-xs font-medium text-slate-600">{label}{hidden && <span className="ml-1 text-amber-700">(after purchase)</span>}
                {k === "lead_exclusivity" ? (
                  <select value={vals[k] ?? ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm" data-testid="market-edit-lead_exclusivity">
                    <option value="">Not set</option><option value="Shared">Shared</option><option value="Exclusive">Exclusive</option>
                  </select>
                ) : <Input value={vals[k] ?? ""} placeholder={k === "lead_source_url" ? "https://..." : undefined} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} className="mt-1" data-testid={`market-edit-${k}`} />}
              </label>
            ))}
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="market-edit-save">Save details</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const money = (k, v) => {
  if (v === "" || isNaN(v)) return String(v);
  if (["requested_loan_amount", "estimated_property_value", "estimated_equity_amount"].includes(k)) return `$${Number(v).toLocaleString()}`;
  if (k === "estimated_current_rate") return `${v}%`;
  return String(v);
};

export const MarketFieldList = ({ src, skipHidden, testId }) => {
  const rows = MARKET_FIELDS.filter(([k, , hidden]) => !(skipHidden && hidden) && src?.[k] !== undefined && src?.[k] !== null && src?.[k] !== "");
  if (!rows.length) return null;
  return (
    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid={testId}>
      {rows.map(([k, label]) => <div key={k}><dt className="text-xs text-slate-400">{label}</dt><dd className="break-words font-medium">{k === "lead_source_url" ? <a href={src[k]} target="_blank" rel="noopener noreferrer" className="break-all text-blue-700 underline" data-testid="lead-source-url-link">{src[k]}</a> : money(k, src[k])}</dd></div>)}
    </dl>
  );
};
